import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import {
  FirstAccess,
  Login,
  InfoBanner,
  BannerModal,
  ChangePassword,
  AddDocument,
  DocumentsList,
  DeleteDocument,
  DeleteSuccessor,
  AddSuccessor,
  SuccessorsList,
} from "./components";
import {
  useIsSuccessorAuthorized,
  useLogoutSuccessor,
  useGetSuccessor,
} from "hooks/successors";
import {
  DashboardTopBar,
  DashboardTopPortal,
  PageHead,
  Button,
  SVG,
} from "shared";
import { useSearchParams } from "react-router-dom";
import { SVG_ENUM } from "enums";
import { useLocation } from "react-router-dom";
import { useGetSettings } from "hooks/settings";
import { SuccessorType } from "types/SuccessorTypes";

import "./SuccessorPage.scss";

type Tab = "docs" | "successors" | "password";
const TABS: { key: Tab; label: string }[] = [
  { key: "docs", label: "Dokumenty" },
  { key: "successors", label: "Sukcesorzy" },
  { key: "password", label: "Zmiana hasła do zakładki" },
];

const isTab = (val: string | null): val is Tab =>
  val === "docs" || val === "successors" || val === "password";

const SuccessorPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const prevAuthorizedRef = useRef<boolean>(false);
  interface ModalManage {
    type: string;
    document: any;
  }
  const [modalManage, setModalManage] = useState<ModalManage>({
    type: "",
    document: null,
  });
  const { data: authorizedUser } = useGetSuccessor();
  const { authorized, refetch, isError } = useIsSuccessorAuthorized();
  const { data: settings } = useGetSettings();
  const { mutate: logoutSuccessor } = useLogoutSuccessor();
  const location = useLocation();
  const isAuthorized = authorized && !isError;
  const isHideInfoBanner = !!settings?.find(
    (s) => s.name === "hide-successor-info-banner"
  )?.valueBoolean;
  const isUserHasSuccessorAccess = !!authorizedUser?.isActive;
  const isBannerOpenModal = modalManage.type === "banner";
  const isAddDocsOpenModal = modalManage.type === "addDocument";
  const isDeleteDocumentOpenModal = modalManage.type === "deleteDocument";
  const isDeleteSuccessorOpenModal = modalManage.type === "deleteSuccessor";
  const isAddSuccessorOpenModal = modalManage.type === "addSuccessor";

  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  useEffect(() => {
    return () => {
      logoutSuccessor();
    };
  }, [logoutSuccessor]);

  const currentTab: Tab = useMemo(() => {
    const fromUrl = searchParams.get("tab");
    return isTab(fromUrl) ? fromUrl : "docs";
  }, [searchParams]);

  const clearModalManage = () => {
    setModalManage({ type: "", document: null });
  };

  useEffect(() => {
    const justLoggedIn = !!authorized && !prevAuthorizedRef.current;

    if (justLoggedIn && currentTab === "password") {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "docs");
      params.set("page", "1");
      setSearchParams(params);
    }

    prevAuthorizedRef.current = !!authorized;
  }, [authorized, currentTab, searchParams, setSearchParams]);

  const handleChangeTab = useCallback(
    (next: Tab) => {
      if (next === currentTab) return;
      const params = new URLSearchParams(searchParams);
      params.set("tab", next);
      params.set("page", "1");
      setSearchParams(params);
    },
    [currentTab, searchParams, setSearchParams]
  );

  const showBaner = () => setModalManage({ type: "banner", document: null });
  const setAddDocs = () => {
    setModalManage({ type: "addDocument", document: null });
  };

  const setDeleteDocument = (document: any) => {
    setModalManage({ type: "deleteDocument", document });
  };

  const setDeleteSuccessor = (document: any) => {
    setModalManage({ type: "deleteSuccessor", document });
  };

  const setAddSuccessor = (successor?: SuccessorType) => {
    setModalManage({ type: "addSuccessor", document: successor || null });
  };

  useEffect(() => {
    if (!!isAuthorized) {
      clearModalManage();
    }
  }, [isAuthorized]);

  return (
    <>
      <PageHead
        title="Sukcesja – bezpieczne przekazanie informacji | App Reliance Pro"
        description="Zachowaj ciągłość decyzji i dostęp do ważnych dokumentów. Dodaj sukcesorów i przechowuj pliki w zakładce Sukcesja."
      />
      <BannerModal
        isOpen={isBannerOpenModal}
        onClose={() => clearModalManage()}
      />
      <AddDocument
        isOpen={isAddDocsOpenModal}
        onClose={() => clearModalManage()}
      />
      <AddSuccessor
        successor={modalManage.document}
        isOpen={isAddSuccessorOpenModal}
        onClose={() => clearModalManage()}
      />
      <DeleteDocument
        documentUuid={modalManage.document}
        isOpen={isDeleteDocumentOpenModal}
        onClose={() => clearModalManage()}
      />
      <DeleteSuccessor
        successorUuid={modalManage.document}
        isOpen={isDeleteSuccessorOpenModal}
        onClose={() => clearModalManage()}
      />
      <div className="successor-page">
        <DashboardTopBar>
          <h1 className="dashboard__header successor-page__top-bar">
            Sukcesja{" "}
            {isAuthorized && isHideInfoBanner && (
              <div className="info-baner__icon icon" onClick={showBaner}>
                <SVG type={SVG_ENUM.INFO} />
              </div>
            )}
          </h1>
          {isAuthorized && (
            <div className="companies__buttons">
              {currentTab === "docs" && (
                <Button
                  className="dashboard__top-button button--rounded"
                  onClick={() => setAddDocs()}
                  label="Dodaj dokument  +"
                />
              )}
              {currentTab === "successors" && (
                <Button
                  className="dashboard__top-button button--rounded"
                  onClick={() => setAddSuccessor()}
                  label="Dodaj kontakt  +"
                />
              )}
            </div>
          )}
        </DashboardTopBar>

        <div className="dashboard-content__wrapper">
          <div className="dashboard-content">
            <DashboardTopPortal>
              {isAuthorized && (
                <div
                  className="dashboard__tabs"
                  role="tablist"
                  aria-label="Zakładki sukcesji"
                >
                  {TABS.map(({ key, label }) => {
                    const active = currentTab === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => handleChangeTab(key)}
                        className={`dashboard__tab ${
                          active ? "dashboard__tab--active" : ""
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </DashboardTopPortal>

            <div className="successor-page__inner">
              <>
                {isUserHasSuccessorAccess ? (
                  isAuthorized ? (
                    <>
                      {!isHideInfoBanner && currentTab !== "password" && (
                        <InfoBanner />
                      )}
                      {currentTab === "docs" && (
                        <DocumentsList
                          setAddDocs={setAddDocs}
                          setDeleteDocument={setDeleteDocument}
                        />
                      )}
                      {currentTab === "successors" && (
                        <SuccessorsList
                          setFinalizePayment={setAddSuccessor}
                          setAddSuccessor={setAddSuccessor}
                          setDeleteSuccessor={setDeleteSuccessor}
                        />
                      )}
                      {currentTab === "password" && <ChangePassword />}
                    </>
                  ) : (
                    <Login />
                  )
                ) : (
                  <FirstAccess authorizedUser={authorizedUser} />
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessorPage;
